// The command API: the seam between the engine and the interfaces.
//
// Interfaces receive this object and nothing else that can make sound. Every
// call is logged before it reaches the engine. This rule is what makes the
// week-9 study valid and what makes the week-12 agent interface possible --
// an LLM issuing commands and a finger on a pad go through the same door.

export const COMMANDS = [
  'trigger', 'setStep', 'setTrack', 'clear', 'setTempo', 'start', 'stop', 'setParam',
  'setMasterVolume',
]

export function createApi({ engine, logger, params }) {
  // Which interface is currently mounted; stamped onto every command.
  let source = 'none'

  const listeners = new Set()

  // `opts.t` lets an interface supply the true instant of the originating
  // input (event.timeStamp, a camera frame time) rather than the moment this
  // function happened to run. The study mode reads it; the engine does not.
  function emit(name, args, opts = {}) {
    const entry = { command: name, args, source, t: opts.t ?? performance.now(), ...opts }
    logger.command(entry)
    for (const fn of listeners) fn(entry)
  }

  const api = {
    // --- the command vocabulary -------------------------------------------
    trigger(sound, opts) {
      emit('trigger', [sound], opts)
      engine.trigger(sound)
    },

    setStep(track, step, on) {
      emit('setStep', [track, step, on])
      engine.setStep(track, step, on)
    },

    setTrack(track, steps) {
      emit('setTrack', [track, steps])
      engine.setTrack(track, steps)
    },

    clear() {
      emit('clear', [])
      engine.clear()
    },

    setTempo(bpm) {
      emit('setTempo', [bpm])
      engine.setTempo(bpm)
      params?.set('engine.tempo', bpm)
    },

    start() {
      emit('start', [])
      engine.start()
    },

    stop() {
      emit('stop', [])
      engine.stop()
    },

    // Goes through the seam like everything else: a participant who changes
    // the volume mid-round should leave a trace in the log.
    setMasterVolume(v) {
      emit('setMasterVolume', [v])
      engine.setMasterVolume(v)
      params?.set('engine.volume', v)
    },

    setParam(sound, name, value) {
      emit('setParam', [sound, name, value])
      engine.setParam(sound, name, value)
      params?.set(`preset.${sound}.${name}`, value)
    },

    // --- read-only state for interfaces to render against -----------------
    get sounds() { return engine.sounds },
    get steps() { return engine.steps },
    get playing() { return engine.playing },
    get tempo() { return engine.tempo },
    getPattern: () => engine.getPattern(),
    getPresets: () => engine.getPresets(),
    onStep: (fn) => engine.onStep(fn),
    onChange: (fn) => engine.onChange(fn),

    // Audio needs a user gesture; interfaces call this from a real handler.
    unlock: () => engine.unlock(),
    get unlocked() { return engine.unlocked },

    // --- plumbing ---------------------------------------------------------
    // Observe every command (used by the study mode and the week-12 agent).
    onCommand(fn) { listeners.add(fn); return () => listeners.delete(fn) },

    // Set by the mode switch, not by interfaces.
    _setSource(s) { source = s },
    get source() { return source },
  }

  return api
}
